import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Button, Space, Typography, Alert } from 'antd';
import { PlayCircleOutlined, GlobalOutlined, RocketOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const LaunchPage = () => {
  const [searchParams] = useSearchParams();
  const [meetUrl, setMeetUrl] = useState('');
  const [frameUrl, setFrameUrl] = useState('');
  const [cam, setCam] = useState('0');
  const [sound, setSound] = useState('0');

  useEffect(() => {
    const meet = (searchParams.get('meet') || '').trim();
    const frame = (searchParams.get('frame') || 'republic-of-arts').trim();
    const camParam = searchParams.get('cam');
    const soundParam = searchParams.get('sound');
    const effectiveCam = camParam || '0';
    const effectiveSound = soundParam || '0';

    setCam(effectiveCam);
    setSound(effectiveSound);

    let meetLink = '';
    let frameLink = '';

    // 构建 Meet URL
    if (meet) {
      // 兼容 WhatsApp 模板中的简化参数：支持 F<meeting_code> / meeting_code / 完整 URL
      let meetValue = meet;
      if (!meetValue.startsWith('http')) {
        if (/^F[a-z0-9]{3}-[a-z0-9]{4}-[a-z0-9]{3}$/i.test(meetValue)) {
          meetValue = meetValue.slice(1);
        }
        meetValue = meetValue.toLowerCase();
      }
      meetLink = meetValue.startsWith('http') ? meetValue : `https://meet.google.com/${meetValue}`;
      setMeetUrl(meetLink);
    }

    // 构建 FrameVR URL（不自动拼接 cam 和 sound 参数）
    if (frame && frame.trim()) {
      let frameSlug = frame.trim();
      const urlMatch = frameSlug.match(/framevr\.io\/([^\/\?]+)/);
      if (urlMatch) {
        frameSlug = urlMatch[1];
      }
      // 确保 frameSlug 不为空且有效
      if (frameSlug && frameSlug !== 'framevr.io' && !frameSlug.startsWith('http')) {
        frameLink = `https://framevr.io/${frameSlug}`;
        // cam/sound 固定参数自动补齐，简化外部链接仅传 meet
        frameLink += `?cam=${effectiveCam}&sound=${effectiveSound}`;
        setFrameUrl(frameLink);
        console.log('FrameVR URL set:', frameLink);
      } else if (frameSlug.startsWith('http')) {
        // 如果已经是完整 URL，直接使用
        frameLink = frameSlug;
        setFrameUrl(frameLink);
        console.log('FrameVR URL set (full URL):', frameLink);
      } else {
        // 如果 frameSlug 无效，使用默认值
        frameLink = 'https://framevr.io/republic-of-arts';
        setFrameUrl(frameLink);
        console.warn('Invalid frame parameter, using default:', frame);
      }
    } else {
      // 如果没有 frame 参数，使用默认值
      frameLink = 'https://framevr.io/republic-of-arts';
      setFrameUrl(frameLink);
      console.log('Frame parameter empty, using default:', frameLink);
    }
  }, [searchParams]);

  const openMeet = () => {
    if (meetUrl) {
      window.open(meetUrl, '_blank');
    }
  };

  const openFrame = () => {
    if (frameUrl) {
      window.open(frameUrl, '_blank');
    }
  };

  // 一键打开 Meet 和 FrameVR
  const openBoth = () => {
    // 使用已构建好的 URL（从 state 中获取，确保是最新的）
    const meetLinkToOpen = meetUrl || '';
    const frameLinkToOpen = frameUrl || '';
    
    console.log('openBoth called, meetUrl:', meetUrl, 'frameUrl:', frameUrl);
    console.log('Opening with meetLinkToOpen:', meetLinkToOpen, 'frameLinkToOpen:', frameLinkToOpen);
    
    // 验证 URL 是否有效
    const isValidUrl = (url) => {
      if (!url || !url.trim()) return false;
      try {
        const urlObj = new URL(url);
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
      } catch {
        return false;
      }
    };
    
    // 在用户点击事件中直接打开两个窗口，避免被浏览器阻止
    // 先打开 Meet（如果有），但不聚焦（保持当前页面焦点）
    if (meetLinkToOpen && isValidUrl(meetLinkToOpen)) {
      try {
        const meetWindow = window.open(meetLinkToOpen, '_blank');
        console.log('Opened Meet:', meetLinkToOpen, 'window:', meetWindow);
        // 不调用 focus()，保持当前页面焦点
        if (!meetWindow) {
          console.warn('Meet window was blocked by browser');
          alert('Google Meet 窗口被浏览器阻止，请允许弹窗后重试');
        }
      } catch (e) {
        console.error('Error opening Meet:', e);
        alert('打开 Google Meet 时出错：' + e.message);
      }
    } else if (meetLinkToOpen) {
      console.warn('Invalid Meet URL:', meetLinkToOpen);
    }
    
    // 立即打开 FrameVR（在同一个用户点击事件中，避免被阻止），并聚焦到 FrameVR
    if (frameLinkToOpen && isValidUrl(frameLinkToOpen)) {
      try {
        console.log('Opening FrameVR:', frameLinkToOpen);
        // 直接调用 window.open，确保在用户交互上下文中
        const frameWindow = window.open(frameLinkToOpen, '_blank');
        console.log('Opened FrameVR:', frameLinkToOpen, 'window:', frameWindow);
        
        if (frameWindow) {
          // 成功打开，聚焦到 FrameVR 窗口
          frameWindow.focus();
        } else {
          // 如果被阻止，使用 setTimeout 重试一次（仍在用户交互上下文中）
          console.warn('FrameVR window was blocked, retrying...');
          setTimeout(() => {
            const retryWindow = window.open(frameLinkToOpen, '_blank');
            if (retryWindow) {
              retryWindow.focus(); // 聚焦到 FrameVR
              console.log('FrameVR opened on retry');
            } else {
              // 如果仍然被阻止，提示用户
              console.warn('FrameVR window still blocked after retry');
              alert(
                'FrameVR 窗口被浏览器阻止。\n\n' +
                '解决方法：\n' +
                '1. 允许此网站的弹窗\n' +
                '2. 点击下方的"打开 FrameVR 虚拟教室"按钮手动打开'
              );
            }
          }, 100);
        }
      } catch (e) {
        console.error('Error opening FrameVR:', e);
        alert('打开 FrameVR 时出错：' + e.message);
      }
    } else {
      console.warn('Invalid FrameVR URL:', frameLinkToOpen);
      // 如果没有有效的 FrameVR URL，尝试使用默认值
      const defaultFrameLink = 'https://framevr.io/republic-of-arts';
      try {
        const defaultWindow = window.open(defaultFrameLink, '_blank');
        if (defaultWindow) {
          defaultWindow.focus();
          console.log('Opened FrameVR with default URL');
        } else {
          alert('FrameVR 链接未配置，且默认链接也被阻止。请检查浏览器弹窗设置。');
        }
      } catch (e) {
        console.error('Error opening default FrameVR:', e);
        alert('FrameVR 链接无效，无法打开。请检查课程配置。');
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          maxWidth: 600,
          width: '100%',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2}>课程启动</Title>
          <Text type="secondary">
            点击下方按钮打开会议和虚拟教室
          </Text>
        </div>

        {(cam === '0' || sound === '0') && (
          <Alert
            message="环境设置建议"
            description={
              <div>
                {cam === '0' && <div>• 建议关闭摄像头以获得更好的性能</div>}
                {sound === '0' && <div>• 建议关闭 FrameVR 声音，使用 Meet 进行语音交流</div>}
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {!meetUrl && (
          <Alert
            message="提示"
            description="未配置 Google Meet 链接，请使用 FrameVR 内的语音功能"
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 一键打开按钮 */}
          {(meetUrl || frameUrl) && (
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              onClick={openBoth}
              block
              style={{ 
                height: 70, 
                fontSize: '18px',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none'
              }}
            >
              🚀 一键启动课程
            </Button>
          )}

          {/* 单独打开按钮 */}
          {(meetUrl || frameUrl) && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 12 }}>
                或单独打开：
              </Text>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {meetUrl && (
                  <Button
                    type="default"
                    size="large"
                    icon={<PlayCircleOutlined />}
                    onClick={openMeet}
                    block
                    style={{ height: 50, fontSize: '15px' }}
                  >
                    打开 Google Meet 会议
                  </Button>
                )}

                {frameUrl && (
                  <Button
                    type="default"
                    size="large"
                    icon={<GlobalOutlined />}
                    onClick={openFrame}
                    block
                    style={{ height: 50, fontSize: '15px' }}
                  >
                    打开 FrameVR 虚拟教室
                  </Button>
                )}
              </Space>
            </div>
          )}

          {!meetUrl && !frameUrl && (
            <Alert
              message="未找到会议信息"
              description="请检查启动链接是否正确"
              type="error"
              showIcon
            />
          )}
        </Space>
      </Card>
    </div>
  );
};

export default LaunchPage;
